import { dialog } from '@forest/shared/solid'
import {
  createEffect,
  on,
  onCleanup,
  Show,
  useContext,
  type Accessor,
  type JSXElement,
} from 'solid-js'
import { closeShop } from '~/lib/close-shop'
import { logProductPurchaseSuccess } from '~/lib/analytics'
import { m } from '~/paraglide/messages'
import type { ShopBalances } from '~/store/session-ledger'
import { OverlayScene } from '../overlay-scene'
import {
  PurchaseBalanceOverlay,
  SHOP_ALERT_DIALOG,
  shopErrorDescription,
} from '../shop-purchase-dialog'
import { PurchaseCommittedContext } from './committed'
import { createPurchaseFlowMachine, type PurchaseAdapter } from './machine'
import { ProcessingOverlay } from './processing-overlay'

/**
 * What a local or HTTP 422 balance shortage does. Tree unlocks escalate to
 * currency-aware top-up navigation and render nothing; the coin exchange
 * shows its own OK-only dialog (GLOSSARY.md: Insufficient purchase balance).
 */
export type InsufficientPolicy =
  | { description: string; kind: 'dialog'; title: string }
  | { kind: 'escalate'; onShort: () => void }

/** The confirmation dialog's copy and priced CTA. */
export interface PurchaseConfirmation {
  cta: JSXElement
  description: string
  title: string
}

/**
 * The Shop purchase flow surface (GLOSSARY.md: Shop purchase flow). The
 * machine owns phase transitions; this adapter maps each dialog phase onto
 * the shared Solid singleton and renders only full-screen outcomes locally.
 */
export function PurchaseFlow(props: {
  adapter: PurchaseAdapter
  /** Renders the inverted coin/gem pills while the flow is confirmable. */
  balancePills?: Accessor<ShopBalances | undefined>
  /**
   * null for a purchase with nothing to confirm — a free unlock (GLOSSARY.md:
   * Free product): the flow submits as it mounts, and a return to
   * confirmation hands back to the surface the purchase started from.
   */
  confirmation: PurchaseConfirmation | null
  insufficient?: InsufficientPolicy
  onCancel: () => void
  onCommitted?: () => void
  /** Follows the in-flight command, for a pending state presented inline. */
  onPendingChange?: (pending: boolean) => void
  reveal: () => JSXElement
  testIdPrefix: string
}) {
  const purchaseCommitted = useContext(PurchaseCommittedContext)
  const machine = createPurchaseFlowMachine({
    adapter: () => props.adapter,
    onCommitted: (productId) => {
      logProductPurchaseSuccess(productId)
      props.onCommitted?.()
      purchaseCommitted(productId)
    },
  })

  const balanceOverlay = () => {
    const balances = props.balancePills
    return balances ? (
      <PurchaseBalanceOverlay
        balances={balances}
        testIdPrefix={`${props.testIdPrefix}-`}
      />
    ) : undefined
  }

  createEffect(
    on(machine.pending, (pending) => props.onPendingChange?.(pending)),
  )

  let submitted = false
  createEffect(
    on(machine.phase, (phase) => {
      switch (phase) {
        case 'confirmation': {
          const confirmation = props.confirmation
          if (!confirmation) {
            if (submitted) {
              dialog.close()
              props.onCancel()
            } else {
              submitted = true
              void machine.confirm()
            }
            return
          }
          void dialog.closeThenOpen({
            ...SHOP_ALERT_DIALOG,
            asyncAction: machine.confirm,
            button: confirmation.cta,
            buttonTestId: `${props.testIdPrefix}-confirm`,
            description: confirmation.description,
            overlayContent: balanceOverlay(),
            secondaryButton: {
              action: props.onCancel,
              button: m.cancel(),
              testId: `${props.testIdPrefix}-cancel`,
            },
            testId: `${props.testIdPrefix}-confirmation`,
            title: confirmation.title,
          })
          return
        }
        case 'submitting':
          return
        case 'processing':
        case 'success':
          // The full-screen surface immediately takes over modality and
          // focus; restoring to the now-covered product would create a jump.
          dialog.close({ restoreFocus: false })
          return
        case 'insufficient': {
          const policy = props.insufficient
          if (policy?.kind === 'dialog') {
            void dialog.closeThenOpen({
              ...SHOP_ALERT_DIALOG,
              action: props.onCancel,
              button: m.ok(),
              buttonTestId: `${props.testIdPrefix}-insufficient-ok`,
              description: policy.description,
              overlayContent: balanceOverlay(),
              testId: `${props.testIdPrefix}-insufficient`,
              title: policy.title,
            })
          } else if (policy?.kind === 'escalate') {
            dialog.close()
            policy.onShort()
          }
          return
        }
        case 'error':
          void dialog.closeThenOpen({
            ...SHOP_ALERT_DIALOG,
            asyncAction: async () => machine.returnToConfirmation(),
            button: m.btn_got_it(),
            buttonTestId: `${props.testIdPrefix}-error-dismiss`,
            description: shopErrorDescription(machine.errorCode()),
            testId: `${props.testIdPrefix}-error`,
            title: m.dialog_something_went_wrong_title(),
          })
          return
        case 'connection':
          void dialog.closeThenOpen({
            ...SHOP_ALERT_DIALOG,
            button: m.ok(),
            buttonTestId: `${props.testIdPrefix}-connection-close`,
            description: m.dialog_connection_issue_description(),
            testId: `${props.testIdPrefix}-connection`,
            title: m.dialog_connection_issue_title(),
            ...(props.adapter.connectionDismissal === 'return-to-start'
              ? {
                  asyncAction: async () => machine.returnToConfirmation(),
                }
              : { action: closeShop }),
          })
          return
        case 'delivery':
          void dialog.closeThenOpen({
            ...SHOP_ALERT_DIALOG,
            action: closeShop,
            button: m.ok(),
            buttonTestId: `${props.testIdPrefix}-delivery-close`,
            description: m.dialog_delivery_issue_description({
              orderid: machine.deliveryOrderId(),
            }),
            testId: `${props.testIdPrefix}-delivery`,
            title: m.dialog_delivery_issue_title(),
          })
          return
        case 'confirming':
          void dialog.closeThenOpen({
            ...SHOP_ALERT_DIALOG,
            action: closeShop,
            button: m.ok(),
            buttonTestId: `${props.testIdPrefix}-confirming-close`,
            description: m.dialog_confirming_perchase_description(),
            testId: `${props.testIdPrefix}-confirming`,
            title: m.dialog_confirming_perchase_title(),
          })
      }
    }),
  )

  onCleanup(() => {
    const phase = machine.phase()
    dialog.close({
      restoreFocus: phase !== 'processing' && phase !== 'success',
    })
  })

  return (
    <>
      <Show when={machine.phase() === 'processing'}>
        <OverlayScene padded>
          <ProcessingOverlay />
        </OverlayScene>
      </Show>
      <Show when={machine.phase() === 'success'}>
        <OverlayScene
          padded
          class="bg-[#51A387] bg-[radial-gradient(circle_at_50%_45%,#FFFEB7_6%,rgba(116,200,171,0.95)_58%,#51A387_100%)]"
        >
          {props.reveal()}
        </OverlayScene>
      </Show>
    </>
  )
}
